// Role IDs for truck yard users (from Firebase)
export const TRUCK_YARD_ADMIN_ROLE_ID = "HiLYcf6ndou53xpXNJ02";
export const TRUCK_YARD_INSPECTOR_ROLE_ID = "Hh1ILyhv8I4tmZZvwwvy";
export const TRUCK_YARD_GUARD_ROLE_ID = "KoNlfjdVBn7xGpZZNk9u";

// Legacy aliases for backward compatibility
export const TRUCK_YARD_SUPERVISOR_ROLE_ID = TRUCK_YARD_INSPECTOR_ROLE_ID;

// Company ID for PTAR (Planta de Tratamiento de Agua Residual)
export const PTAR_COMPANY_ID = "zQ4Xpa88F7RstUvLKc5";

// Apps allowed for each role
// Admin: truck-yard (patios) + inspecciones (full access) + admin (fleet control, read-only)
export const ADMIN_ALLOWED_APPS = ["truck-yard", "inspecciones", "admin"];
// Inspector: only inspecciones
export const INSPECTOR_ALLOWED_APPS = ["inspecciones"];
// Guard: only truck-yard (patios) - access section only
export const GUARD_ALLOWED_APPS = ["truck-yard"];

// Legacy aliases
export const SUPERVISOR_ALLOWED_APPS = INSPECTOR_ALLOWED_APPS;

// Menu items allowed for each role in truck yard
// Admin: all menu items (yards, personal, access, inspections)
export const ADMIN_ALLOWED_MENU = [
  "yards",
  "personal",
  "access",
  "inspections",
];
// Inspector: only inspections menu
export const INSPECTOR_ALLOWED_MENU = ["inspections"];
// Guard: only access (to their assigned yards)
export const GUARD_ALLOWED_MENU = ["access"];

// Legacy aliases
export const SUPERVISOR_ALLOWED_MENU = INSPECTOR_ALLOWED_MENU;

/**
 * Get the user's assigned yard IDs from localStorage
 */
export function getUserAssignedYardIds(): string[] {
  try {
    const yardIds = localStorage.getItem("userYardIds");
    if (yardIds) {
      return JSON.parse(yardIds);
    }
  } catch (e) {
    console.error("Error parsing userYardIds:", e);
  }
  return [];
}

/**
 * Get the user's single assigned yard ID (for guards)
 */
export function getUserAssignedYardId(): string {
  return localStorage.getItem("userYardId") || "";
}

/**
 * Hook to check if the current user has a truck yard specific role
 * and get their permissions based on role ID
 */
export function useTruckYardRole() {
  const userRoleId = localStorage.getItem("userRoleId") || "";
  const userCompanyId =
    localStorage.getItem("userCompanyId") ||
    localStorage.getItem("companyId") ||
    "";

  // Check if user belongs to PTAR company
  const isPTARCompany = userCompanyId === PTAR_COMPANY_ID;

  const isAdmin = userRoleId === TRUCK_YARD_ADMIN_ROLE_ID;
  const isInspector = userRoleId === TRUCK_YARD_INSPECTOR_ROLE_ID;
  const isGuard = userRoleId === TRUCK_YARD_GUARD_ROLE_ID;

  // Legacy alias
  const isSupervisor = isInspector;

  // User is a truck yard user if they have any of these roles AND belong to PTAR company
  const isTruckYardUser = isPTARCompany && (isAdmin || isInspector || isGuard);

  // Get assigned yard IDs based on role
  const getAssignedYardIds = (): string[] => {
    if (isAdmin) {
      return getUserAssignedYardIds();
    }
    if (isSupervisor) {
      return getUserAssignedYardIds();
    }
    if (isGuard) {
      const yardId = getUserAssignedYardId();
      return yardId ? [yardId] : [];
    }
    return []; // Empty means no restrictions (reseller admin)
  };

  // Check if user has yard restrictions
  const hasYardRestrictions = (): boolean => {
    return isAdmin || isSupervisor || isGuard;
  };

  // Check if a specific yard is allowed
  const isYardAllowed = (yardId: string): boolean => {
    if (!hasYardRestrictions()) return true;
    const assignedYardIds = getAssignedYardIds();
    return assignedYardIds.includes(yardId);
  };

  // Get allowed apps based on role (only for PTAR company users)
  const getAllowedApps = (): string[] | null => {
    if (!isPTARCompany) return null; // Non-PTAR users have no restrictions from this hook
    if (isAdmin) return ADMIN_ALLOWED_APPS;
    if (isInspector) return INSPECTOR_ALLOWED_APPS;
    if (isGuard) return GUARD_ALLOWED_APPS;
    return null; // null means all apps allowed
  };

  // Get allowed menu items based on role (only for PTAR company users)
  const getAllowedMenuItems = (): string[] | null => {
    if (!isPTARCompany) return null; // Non-PTAR users have no restrictions from this hook
    if (isAdmin) return ADMIN_ALLOWED_MENU;
    if (isInspector) return INSPECTOR_ALLOWED_MENU;
    if (isGuard) return GUARD_ALLOWED_MENU;
    return null; // null means all menu items allowed
  };

  // Check if a specific app is allowed
  const isAppAllowed = (appId: string): boolean => {
    const allowedApps = getAllowedApps();
    if (!allowedApps) return true; // No restrictions
    return allowedApps.includes(appId);
  };

  // Check if a specific menu item is allowed
  const isMenuItemAllowed = (menuId: string): boolean => {
    const allowedMenuItems = getAllowedMenuItems();
    if (!allowedMenuItems) return true; // No restrictions
    return allowedMenuItems.includes(menuId);
  };

  // Get the default route for truck yard roles (only for PTAR company users)
  const getDefaultRoute = (): string => {
    if (!isPTARCompany) return "/map"; // Non-PTAR users go to map
    if (isAdmin) return "/truck-yard/access"; // Admin goes to access (full control)
    if (isInspector) return "/inspecciones"; // Inspector goes to inspections
    if (isGuard) return "/truck-yard/access"; // Guard goes to access
    return "/map"; // Default for other roles
  };

  return {
    userRoleId,
    userCompanyId,
    isPTARCompany,
    isAdmin,
    isInspector,
    isSupervisor, // Legacy alias for isInspector
    isGuard,
    isTruckYardUser,
    getAllowedApps,
    getAllowedMenuItems,
    isAppAllowed,
    isMenuItemAllowed,
    getDefaultRoute,
    getAssignedYardIds,
    hasYardRestrictions,
    isYardAllowed,
  };
}

export default useTruckYardRole;
